import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { workspaceService } from "../../services/workspaceService";
import { billingAPI } from "../../services/api";

// Lets the billing owner hand the account to an existing admin. Only rendered
// when the current user actually owns a billing account.
const TransferOwnershipCard = () => {
  const { user } = useAuth();
  const [ownedAccountId, setOwnedAccountId] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    let active = true;
    workspaceService
      .getAvailableBillingAccounts()
      .then((accounts) => {
        if (!active) return;
        const owned = (accounts || []).find((a) => a.owner_id === user.id);
        setOwnedAccountId(owned?.id || null);
        if (owned) {
          billingAPI
            .getOwnerCandidates(owned.id)
            .then((c) => active && setCandidates(c || []))
            .catch(() => active && setCandidates([]));
        }
      })
      .catch(() => active && setOwnedAccountId(null));
    return () => {
      active = false;
    };
  }, [user?.id]);

  // Not a billing owner → nothing to transfer.
  if (!ownedAccountId) return null;

  const transfer = async () => {
    setSubmitting(true);
    setMsg(null);
    try {
      await billingAPI.transferOwnership(ownedAccountId, Number(selected));
      setMsg({ type: "success", text: "Ownership transferred. You are now an admin of this account." });
      setConfirming(false);
      setSelected("");
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.error || "Transfer failed" });
    } finally {
      setSubmitting(false);
    }
  };

  const selectedName = candidates.find((c) => String(c.id) === String(selected));

  return (
    <div className="bg-card rounded-lg border border-border p-4">
      <h3 className="text-sm font-semibold text-foreground">Transfer ownership</h3>
      <p className="text-sm text-muted-foreground mt-1">
        Hand this billing account to another admin. They become the owner; you become an admin.
        Billing and all workspaces stay on the same account.
      </p>

      {msg && (
        <p className={`mt-3 text-sm ${msg.type === "error" ? "text-red-600" : "text-green-700"}`}>{msg.text}</p>
      )}

      {candidates.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">
          No eligible admins. Promote a member to admin first, then transfer.
        </p>
      ) : (
        <div className="mt-4 flex items-end gap-3">
          <div className="flex-1 max-w-sm">
            <label className="block text-sm font-medium text-foreground mb-1">New owner</label>
            <select
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              className="block w-full rounded-lg border-input shadow-sm focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm"
            >
              <option value="">Select an admin…</option>
              {candidates.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.email})
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            disabled={!selected}
            onClick={() => setConfirming(true)}
            className="px-4 py-2 text-sm font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
          >
            Transfer
          </button>
        </div>
      )}

      {confirming && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-card rounded-xl shadow-xl max-w-md w-full p-4 mx-4">
            <h4 className="text-sm font-semibold text-foreground">Transfer ownership?</h4>
            <p className="text-sm text-muted-foreground mt-2">
              {selectedName?.name} ({selectedName?.email}) will become the owner of this billing account, and
              you will be demoted to admin on every workspace you currently own. This can't be undone by you
              afterward.
            </p>
            <div className="flex justify-end gap-3 mt-5">
              <button
                type="button"
                onClick={() => setConfirming(false)}
                className="px-4 py-2 text-sm font-medium rounded-lg text-foreground bg-card border border-input hover:bg-muted/50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={transfer}
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
              >
                {submitting ? "Transferring…" : "Confirm transfer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransferOwnershipCard;
